import { useMemo, useState } from 'react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { ko } from 'date-fns/locale';

export const DATE_KEY_FORMAT = 'yyyy-MM-dd';

interface CalendarMultiSelectProps {
  /** 선택된 날짜 키('yyyy-MM-dd') 목록 */
  selected: readonly string[];
  /** 날짜 셀 탭 → 선택 토글 */
  onToggle: (dateKey: string) => void;
  /** 이 날짜 이전은 선택 불가 (기본: 오늘) */
  minDate?: Date;
}

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

// 모바일 달력 — 여러 날을 탭으로 다중 선택. 과거는 비활성, 일요일 시작.
export function CalendarMultiSelect({ selected, onToggle, minDate }: CalendarMultiSelectProps) {
  const min = startOfDay(minDate ?? new Date());
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  // 표시 월: 첫 선택 날짜의 월, 없으면 최소 날짜(보통 오늘)의 월
  const [cursor, setCursor] = useState<Date>(() => {
    const first = selected[0];
    return startOfMonth(first ? parseISO(first) : min);
  });

  const days = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(cursor), { weekStartsOn: 0 });
    const gridEnd = endOfWeek(endOfMonth(cursor), { weekStartsOn: 0 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [cursor]);

  const canGoPrev = !isSameMonth(cursor, min) && !isBefore(cursor, startOfMonth(min));

  return (
    <div className="select-none">
      {/* 월 네비게이션 */}
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setCursor((c) => addMonths(c, -1))}
          disabled={!canGoPrev}
          aria-label="이전 달"
          className="pressable flex h-9 w-9 items-center justify-center rounded-full text-ink-muted hover:bg-surface-muted disabled:opacity-30 focus-visible:outline-none focus-visible:shadow-focus-ring"
        >
          ‹
        </button>
        <span className="text-ui font-semibold text-ink">{format(cursor, 'yyyy년 M월', { locale: ko })}</span>
        <button
          type="button"
          onClick={() => setCursor((c) => addMonths(c, 1))}
          aria-label="다음 달"
          className="pressable flex h-9 w-9 items-center justify-center rounded-full text-ink-muted hover:bg-surface-muted focus-visible:outline-none focus-visible:shadow-focus-ring"
        >
          ›
        </button>
      </div>

      {/* 요일 헤더 */}
      <div className="grid grid-cols-7 text-center text-tag font-semibold text-ink-faint">
        {WEEKDAYS.map((w, i) => (
          <span key={w} className={i === 0 ? 'text-error/70' : undefined}>
            {w}
          </span>
        ))}
      </div>

      {/* 날짜 그리드 */}
      <div className="mt-1 grid grid-cols-7 gap-y-1">
        {days.map((d) => {
          const key = format(d, DATE_KEY_FORMAT);
          const inMonth = isSameMonth(d, cursor);
          const disabled = isBefore(d, min);
          const on = selectedSet.has(key);
          const isSunday = d.getDay() === 0;
          return (
            <div key={key} className="flex items-center justify-center py-0.5">
              <button
                type="button"
                disabled={disabled}
                aria-pressed={on}
                aria-label={format(d, 'M월 d일 (EEE)', { locale: ko })}
                onClick={() => onToggle(key)}
                className={`flex h-10 w-10 items-center justify-center rounded-full text-ui font-semibold transition-all duration-150 ease-airbnb focus-visible:outline-none focus-visible:shadow-focus-ring ${
                  on
                    ? 'bg-primary text-white'
                    : disabled
                      ? 'cursor-not-allowed text-ink-disabled'
                      : isSameDay(d, min)
                        ? 'text-primary hover:bg-primary-soft'
                        : `${inMonth ? (isSunday ? 'text-error/80' : 'text-ink') : 'text-ink-disabled'} hover:bg-surface-muted`
                }`}
              >
                {format(d, 'd')}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
